/* AB ab_flex_offer — sunsets 2026-10-31, delete src/pages/AB_FlexOfferSuivi/ */
/* eslint-disable max-lines-per-function, no-restricted-syntax --
 * Self-contained experimental page reproducing the standalone POC. Single-file
 * JSX + inline SVG strokes are intentional (deleted in one shot at sunset). */

import { useEffect, useMemo, useState } from 'react';

import { getAgentDetailsFromEtsToken } from '../../resources/login/operations';

import {
    AB_agencyInitials,
    AB_BookingRow,
    AB_computeKpis,
    AB_deadlineBadgeInfo,
    AB_filterRows,
    AB_formatAmount,
    AB_formatDate,
    AB_paymentBadgeInfo,
    AB_parseCSV,
    AB_PaymentFilter,
    AB_rowsToCsv,
    AB_SortColumn,
    AB_SortDirection,
    AB_sortRows,
    AB_statusBadgeClass,
    AB_tripTagInfo,
} from './AB_flexOfferSuiviOperations';
import { AB_FLEX_OFFER_SUIVI_CSS } from './AB_flexOfferSuiviStyles';

const CSV_URL =
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vQlLeGrk2WoP7c81AXIWBkZwUdX2DVp-_AXLilqOmQscJFi6kjaf_X84aBoT9-_Urgl3fu4kEaIJW_t/pub?gid=1832356639&single=true&output=csv';
const RM_BASE = 'https://travelsuite.evaneos.com/request-manager/dossier';

type ColumnDef = { col: AB_SortColumn; label: string; align?: 'right' };
const COLUMNS: ColumnDef[] = [
    { col: 'request_id', label: 'Booking #' },
    { col: 'country_name', label: 'Destination' },
    { col: 'sale_date', label: 'Sale date' },
    { col: 'trip_arrival_date', label: 'Trip date' },
    { col: 'cancellation_deadline', label: 'Cancellation deadline' },
    { col: 'status_grouped', label: 'Status' },
    { col: 'status_name_legacy', label: 'Payment' },
    { col: 'amount_paid', label: 'Paid', align: 'right' },
    { col: 'remaining_balance_to_pay', label: 'Remaining', align: 'right' },
    { col: 'trip_price', label: 'Total', align: 'right' },
];

export function AB_FlexOfferSuiviPage() {
    const agencyIdNumber = getAgentDetailsFromEtsToken()?.agencyId ?? null;
    const agencyId = agencyIdNumber === null ? null : String(agencyIdNumber);

    const [rows, setRows] = useState<AB_BookingRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState('');
    const [paymentFilter, setPaymentFilter] = useState<AB_PaymentFilter>('');
    const [showTravelled, setShowTravelled] = useState(false);
    const [sortColumn, setSortColumn] = useState<AB_SortColumn>('cancellation_deadline');
    const [sortDirection, setSortDirection] = useState<AB_SortDirection>('asc');

    useEffect(() => {
        if (!agencyId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        const loadData = async () => {
            try {
                const res = await fetch(CSV_URL);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const text = await res.text();
                const parsed = AB_parseCSV(text).filter((r) => r.agency_id === agencyId);
                if (!cancelled) {
                    setRows(parsed);
                    setLoading(false);
                }
            } catch {
                if (!cancelled) {
                    setError(true);
                    setLoading(false);
                }
            }
        };
        void loadData();
        return () => {
            cancelled = true;
        };
    }, [agencyId]);

    const filteredRows = useMemo(
        () => AB_filterRows(rows, search, paymentFilter, showTravelled),
        [rows, search, paymentFilter, showTravelled],
    );
    const sortedRows = useMemo(
        () => AB_sortRows(filteredRows, sortColumn, sortDirection),
        [filteredRows, sortColumn, sortDirection],
    );
    const kpis = useMemo(() => AB_computeKpis(rows), [rows]);
    const agencyName = rows[0]?.agency_name ?? '';

    const handleSort = (col: AB_SortColumn) => {
        if (col === sortColumn) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(col);
            setSortDirection('asc');
        }
    };

    const handleExportCsv = () => {
        const csv = AB_rowsToCsv(sortedRows);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `flex-offer-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="fo-loading">
                    <div className="fo-spinner" />
                    Loading bookings…
                </div>
            );
        }
        if (error) {
            return (
                <div className="fo-error-state">
                    <div className="fo-error-icon">
                        <svg
                            width="22"
                            height="22"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    </div>
                    <div className="fo-error-title">Unable to load your bookings</div>
                    <p className="fo-error-desc">
                        We couldn&apos;t retrieve your Spring Flex Offer data. This may be a
                        temporary issue — please try refreshing the page. If the problem persists,
                        please reach out to your Evaneos contact.
                    </p>
                </div>
            );
        }
        if (sortedRows.length === 0) {
            return (
                <div className="fo-empty-state">
                    <div className="fo-empty-icon">
                        <svg
                            width="22"
                            height="22"
                            fill="none"
                            stroke="#00B48D"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14,2 14,8 20,8" />
                        </svg>
                    </div>
                    <div className="fo-empty-title">No bookings found</div>
                    <p className="fo-empty-desc">
                        No bookings match your search. Try adjusting the filters.
                    </p>
                </div>
            );
        }
        return (
            <>
                <div className="fo-table-scroll">
                    <table>
                        <thead>
                            <tr>
                                {COLUMNS.map(({ col, label, align }) => {
                                    let sortIcon: string;
                                    if (sortColumn !== col) {
                                        sortIcon = '↕';
                                    } else {
                                        sortIcon = sortDirection === 'asc' ? '↑' : '↓';
                                    }
                                    return (
                                        <th
                                            key={col}
                                            className={sortColumn === col ? 'fo-sorted' : ''}
                                            style={
                                                align === 'right'
                                                    ? { textAlign: 'right' }
                                                    : undefined
                                            }
                                            onClick={() => handleSort(col)}
                                        >
                                            {label} <span className="fo-sort-icon">{sortIcon}</span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((r) => {
                                const deadline = AB_deadlineBadgeInfo(r.days_until_deadline);
                                const tripTag = AB_tripTagInfo(r.status_grouped);
                                const payment = AB_paymentBadgeInfo(r.status_name_legacy);
                                return (
                                    <tr
                                        key={r.request_id}
                                        className={
                                            r.status_grouped === 'travelled'
                                                ? 'fo-is-travelled'
                                                : ''
                                        }
                                    >
                                        <td>
                                            <a
                                                className="fo-link-dossier"
                                                href={`${RM_BASE}/${r.request_id}/info`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                #{r.request_id}
                                            </a>
                                        </td>
                                        <td className="fo-destination-cell">
                                            {r.country_name || '—'}
                                        </td>
                                        <td>{AB_formatDate(r.sale_date)}</td>
                                        <td>
                                            {AB_formatDate(r.trip_arrival_date)}
                                            {tripTag && (
                                                <span className={tripTag.className}>
                                                    {tripTag.label}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {AB_formatDate(r.cancellation_deadline)}
                                            {deadline && (
                                                <>
                                                    {' '}
                                                    <span
                                                        className={`fo-badge fo-badge-${deadline.variant}`}
                                                    >
                                                        {deadline.label}
                                                    </span>
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className={`fo-status-badge ${AB_statusBadgeClass(r.status_grouped)}`}
                                            >
                                                {r.status_grouped}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className={`fo-status-badge ${payment.className}`}
                                            >
                                                {payment.label}
                                            </span>
                                        </td>
                                        <td className="fo-amount-cell">
                                            {AB_formatAmount(r.amount_paid)}
                                        </td>
                                        <td
                                            className="fo-amount-cell"
                                            style={{ color: 'var(--fo-text-muted)' }}
                                        >
                                            {AB_formatAmount(r.remaining_balance_to_pay)}
                                        </td>
                                        <td className="fo-amount-cell">
                                            {AB_formatAmount(r.trip_price)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="fo-table-footer">
                    {sortedRows.length} booking{sortedRows.length > 1 ? 's' : ''} displayed ·{' '}
                    {rows.length} total
                </div>
            </>
        );
    };

    return (
        <div className="fo-root" data-spec-id="flex-offer-suivi-page">
            <style>{AB_FLEX_OFFER_SUIVI_CSS}</style>

            <header className="fo-header">
                <div className="fo-header-left">
                    <span className="fo-header-product">Spring Flex Offer · Booking Tracker</span>
                </div>
                <div className="fo-header-right">
                    {agencyName && (
                        <div className="fo-agency-chip">
                            <div className="fo-agency-avatar">{AB_agencyInitials(agencyName)}</div>
                            <span className="fo-agency-label">{agencyName}</span>
                        </div>
                    )}
                </div>
            </header>

            <main className="fo-main">
                <div className="fo-page-header">
                    <div>
                        <h1 className="fo-page-title">My Spring Flex Offer Bookings</h1>
                        <div className="fo-page-meta">
                            <div className="fo-sync-dot" />
                            Data updated daily between 7am and 8am (Paris time)
                        </div>
                    </div>
                </div>

                <div className="fo-kpi-row">
                    <div className="fo-kpi fo-kpi-main">
                        <div className="fo-kpi-label">Spring Flex Offer Eligible Bookings</div>
                        <div className="fo-kpi-number">{loading ? '—' : kpis.total}</div>
                        <div className="fo-kpi-hint">
                            {loading ? '—' : kpis.stillCancellable} still cancellable ·{' '}
                            {loading ? '—' : kpis.pastDeadline} with overdue deadline
                        </div>
                    </div>
                    <div className="fo-kpi fo-kpi-main-expo">
                        <div className="fo-kpi-label">Cancellation Exposure</div>
                        <div className="fo-expo-grid">
                            <div className="fo-expo-block fo-is-primary">
                                <div className="fo-expo-block-label">Current Exposure</div>
                                <div className="fo-expo-block-value">
                                    {loading ? '—' : kpis.expoNow}
                                </div>
                                <div className="fo-expo-block-hint">
                                    amount collected to be refunded
                                </div>
                            </div>
                            <div className="fo-expo-block">
                                <div className="fo-expo-block-label">Maximum Exposure</div>
                                <div className="fo-expo-block-value">
                                    {loading ? '—' : kpis.expoMax}
                                </div>
                                <div className="fo-expo-block-hint">
                                    Amount to be refund if booking is fully paid
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="fo-card">
                    <div className="fo-toolbar">
                        <div className="fo-toolbar-left">
                            <div className="fo-search-wrap">
                                <span className="fo-search-icon">
                                    <svg
                                        width="14"
                                        height="14"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="m21 21-4.35-4.35" />
                                    </svg>
                                </span>
                                <input
                                    className="fo-search-input"
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by booking # or destination…"
                                    autoComplete="off"
                                />
                            </div>
                            <select
                                value={paymentFilter}
                                onChange={(e) =>
                                    setPaymentFilter(e.target.value as AB_PaymentFilter)
                                }
                                aria-label="Filter by payment status"
                            >
                                <option value="">All payments</option>
                                <option value="fully_paid">Fully paid</option>
                                <option value="account_done">Partially paid</option>
                                <option value="deposit_paid">Deposit paid</option>
                            </select>
                            <label className="fo-checkbox-wrap">
                                <input
                                    type="checkbox"
                                    checked={showTravelled}
                                    onChange={(e) => setShowTravelled(e.target.checked)}
                                />
                                <span>Show completed trips</span>
                            </label>
                        </div>
                        <button
                            type="button"
                            className="fo-btn"
                            onClick={handleExportCsv}
                            disabled={sortedRows.length === 0}
                        >
                            <svg
                                width="14"
                                height="14"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7,10 12,15 17,10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Export CSV
                        </button>
                    </div>

                    {renderContent()}
                </div>
            </main>
        </div>
    );
}
